import axios from "axios";
import api from "../utils/axiosConfig";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import HearingForm from "./HearingForm";
import Header from "./Header";
import { useAuth } from "../auth/useAuth.hook";
import {
  StateType,
  AgentFormProps,
  FormErrors,
  emptyStateAgentForm,
} from "../utils/interfaces/AgentForm";
import {
  capitalizeFirstLetter,
  cpfMask,
  isAdult,
  phoneMask,
  toastOptions,
} from "../utils/helpers";
import {
  canonicalizePixPhone,
  displayPixPhone,
  isValidCPF,
  isValidPhone,
  isValidEmail,
  normalizePixKeyInput,
  validatePixKey,
} from "../utils/pixValidation";
import { uploadPendingPreppoCertificate } from "../utils/preppoCertificateUpload";
import { uploadPendingAgentCv } from "../utils/agentCvUpload";

const apiUri = import.meta.env.VITE_API_URI as string;

interface CatalogState {
  id: number;
  slug: string;
  name: string;
}

interface CatalogCity {
  id: number;
  name: string;
}

const foldCityName = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const formatCep = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const requiredFields = [
  "name",
  "cpf",
  "email",
  "phone",
  "birthDate",
  "gender",
  "pixKey",
  "address.postalCode",
  "address.street",
  "address.number",
  "address.neighborhood",
  "address.city",
  "address.state",
  "preppoCertificateLink",
];

const AgentForm: React.FC<AgentFormProps> = ({ agent }) => {
  const [formData, setFormData] = useState<StateType>(emptyStateAgentForm);
  const [pixType, setPixType] = useState<string>("");
  const [pixPhoneFocused, setPixPhoneFocused] = useState(false);
  const [certificateFileName, setCertificateFileName] = useState<string>("");
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [cvFileName, setCvFileName] = useState<string>("");
  const [uploadingCv, setUploadingCv] = useState(false);

  const [errors, setErrors] = useState<FormErrors>({});
  const { accessToken } = useAuth();
  const [catalogStates, setCatalogStates] = useState<CatalogState[]>([]);
  const [catalogCities, setCatalogCities] = useState<CatalogCity[]>([]);
  const [citiesLoadError, setCitiesLoadError] = useState(false);
  const [addressFromCep, setAddressFromCep] = useState(false);
  const postalCodeRef = useRef(formData.address.postalCode);
  const rejectedCepRef = useRef<string | null>(null);
  const citiesLoadIdRef = useRef(0);
  const cepLoadIdRef = useRef(0);
  const catalogUfRef = useRef("");
  postalCodeRef.current = formData.address.postalCode;

  const handlePixTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPixType(e.target.value);
    setPixPhoneFocused(false);
    setFormData((prev) => ({ ...prev, pixKey: "" }));
    setErrors((prev) => ({ ...prev, pixKey: undefined }));
  };

  const getAgent = async () => {
    try {
      const { data } = await api.get(`agents/me`);
      rejectedCepRef.current = null;
      setFormData({
        ...emptyStateAgentForm,
        ...data,
        address: {
          ...emptyStateAgentForm.address,
          ...data?.address,
          postalCode: formatCep(data?.address?.postalCode || ""),
        },
      });
    } catch (error) {
      console.error("Error fetching agent data:", error);
    }
  };

  useEffect(() => {
    if (accessToken) {
      getAgent();
    }
  }, [accessToken]);

  useEffect(() => {
    api
      .get(`${apiUri}address/states`)
      .then((response) => {
        setCatalogStates(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching states:", error);
        toast.error(
          "Não foi possível carregar os estados. Atualize a página para tentar de novo.",
          toastOptions
        );
      });
  }, []);

  const setStateWrapper = (state: any) => {
    setFormData(state);
  };

  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );
  const minDate = eighteenYearsAgo.toISOString().split("T")[0];

  const handlePixKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pixKey = normalizePixKeyInput(e.target.value, pixType);
    setFormData({ ...formData, pixKey });
  };

  const handleCertificateFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingCertificate(true);
    try {
      const objectKey = await uploadPendingPreppoCertificate(file);
      setCertificateFileName(file.name);
      setFormData((prev) => ({
        ...prev,
        preppoCertificateLink: objectKey,
        hasPreppoCertificate: true,
      }));
      validateRequiredFields("preppoCertificateLink", objectKey);
      toast.success("Certificado enviado com sucesso", toastOptions);
    } catch (error) {
      console.error("Error uploading certificate:", error);
      // Keep any previously successful certificate; only the replacement failed.
      const message =
        error instanceof Error
          ? error.message
          : "Erro ao enviar certificado. Tente novamente.";
      toast.error(message, toastOptions);
    } finally {
      setUploadingCertificate(false);
      e.target.value = "";
    }
  };

  const handleCvFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingCv(true);
    try {
      const objectKey = await uploadPendingAgentCv(file);
      setCvFileName(file.name);
      setFormData((prev) => ({
        ...prev,
        cvLink: objectKey,
      }));
      toast.success("Currículo enviado com sucesso", toastOptions);
    } catch (error) {
      console.error("Error uploading CV:", error);
      // Keep any previously successful CV; only the replacement failed.
      const message =
        error instanceof Error
          ? error.message
          : "Erro ao enviar currículo. Tente novamente.";
      toast.error(message, toastOptions);
    } finally {
      setUploadingCv(false);
      e.target.value = "";
    }
  };

  const handleChange = async (
    e:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      setFormData({ ...formData, [name]: e.target.checked });
      validateRequiredFields(name, e.target.value ? "true" : "");
      return;
    }
    if (type === "radio" && e.target instanceof HTMLInputElement) {
      setFormData({ ...formData, [name]: e.target.value === "true" });
      validateRequiredFields(name, e.target.value === "true" ? "true" : "");
      return;
    }
    if (name === "cpf") {
      const maskedCpf = cpfMask(value);
      setFormData({ ...formData, [name]: maskedCpf });
      validateRequiredFields(name, maskedCpf);
      return;
    }
    if (name === "phone") {
      setFormData({ ...formData, [name]: phoneMask(value) });
      validateRequiredFields(name, phoneMask(value));
      return;
    }
    setFormData({ ...formData, [name]: value });
    validateRequiredFields(name, value);
  };

  const checkCpfExists = async (cpf: string) => {
    try {
      const response = await axios.get(`${apiUri}agents?cpf=${cpf}`);
      return response.data.length > 0;
    } catch (error) {
      console.error("Error checking CPF:", error);
      return false;
    }
  };

  const validateRequiredFields = (name: string, value: string) => {
    const newErrors: any = { ...errors };
    let errorKey = name;
    if (name.startsWith("address.")) {
      const [, field] = name.split(".");
      errorKey = `address${capitalizeFirstLetter(field)}`;
    }

    if (requiredFields.includes(name) && !value) {
      newErrors[errorKey] = "Este campo é obrigatório";
    } else {
      delete newErrors[errorKey];
    }
    setErrors(newErrors);
  };

  const validateForm = async () => {
    const newErrors: any = {};

    // Validar e-mail
    if (!isValidEmail(formData.email)) {
      newErrors.email = "E-mail inválido";
    }

    // Validar CPF
    if (!isValidCPF(formData.cpf)) {
      newErrors.cpf = "CPF inválido";
    }

    // Validar idade
    if (!isAdult(formData.birthDate)) {
      newErrors.birthDate = "Você deve ser maior de 18 anos";
    }

    // Validar certificado Preppo (sempre obrigatório)
    if (!formData.preppoCertificateLink) {
      newErrors.preppoCertificateLink = "Certificado é obrigatório";
    }

    // Validar aceite dos termos
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms =
        "Você deve concordar com os Termos de Uso e a Política de Privacidade para continuar";
    }

    // Validar chave PIX
    const pixKey =
      pixType === "Telefone"
        ? canonicalizePixPhone(formData.pixKey)
        : formData.pixKey.trim();
    if (!pixType || !pixKey) {
      newErrors.pixKey = "Chave PIX e tipo são obrigatórios";
    } else {
      const pixValidation = validatePixKey(pixKey, pixType);
      if (!pixValidation.isValid) {
        newErrors.pixKey = pixValidation.error;
      }
    }

    // Validar telefone
    if (!isValidPhone(formData.phone)) {
      newErrors.phone = "Telefone inválido (DDD + número)";
    }

    const cepDigits = formData.address.postalCode.replace(/\D/g, "");
    if (cepDigits.length !== 8) {
      newErrors.addressPostalCode = "CEP deve ter 8 dígitos";
    } else if (rejectedCepRef.current === cepDigits) {
      newErrors.addressPostalCode = "CEP inválido";
    }

    if (!formData.address.state) {
      newErrors.addressState = "Este campo é obrigatório";
    }

    if (citiesLoadError) {
      newErrors.addressCity =
        "Não foi possível carregar as cidades. Tente novamente.";
    } else if (!formData.address.city || !formData.address.cityEntity?.id) {
      newErrors.addressCity = "Selecione a cidade";
    } else if (formData.address.city.includes("/")) {
      newErrors.addressCity = "Selecione a cidade na lista";
    }

    // Verificar se CPF já existe
    if (!newErrors.cpf) {
      const cpfExists = await checkCpfExists(formData.cpf);
      if (cpfExists) {
        newErrors.cpf = "CPF já cadastrado";
        toast.error("CPF já cadastrado", toastOptions);
      }
    }

    // Atualizar estado de erros para exibir na UI
    setErrors(newErrors);

    // Exibir toast com o primeiro erro encontrado
    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0] as string;
      toast.error(firstError, toastOptions);
    }

    return Object.keys(newErrors).length === 0;
  };

  const loadCitiesForUf = async (
    uf: string,
    statesList?: CatalogState[]
  ): Promise<{ cities: CatalogCity[]; failed: boolean }> => {
    const loadId = ++citiesLoadIdRef.current;
    try {
      let states = statesList ?? catalogStates;
      if (!states.length) {
        const response = await api.get(`${apiUri}address/states`);
        states = response.data || [];
        if (loadId === citiesLoadIdRef.current) {
          setCatalogStates(states);
        }
      }
      const state = states.find(
        (item) => item.slug?.toUpperCase() === uf.toUpperCase()
      );
      if (!state) {
        if (loadId === citiesLoadIdRef.current) {
          catalogUfRef.current = "";
          setCatalogCities([]);
          setCitiesLoadError(false);
        }
        return { cities: [], failed: false };
      }
      const response = await api.get(
        `${apiUri}address/states/${state.id}/cities`
      );
      const cities = response.data || [];
      if (loadId === citiesLoadIdRef.current) {
        catalogUfRef.current = uf.toUpperCase();
        setCatalogCities(cities);
        setCitiesLoadError(false);
        setErrors((prev) => {
          if (
            prev.addressCity !==
            "Não foi possível carregar as cidades. Tente novamente."
          ) {
            return prev;
          }
          const next = { ...prev };
          delete next.addressCity;
          return next;
        });
      }
      return { cities, failed: false };
    } catch (error) {
      console.error("Error fetching cities:", error);
      if (loadId === citiesLoadIdRef.current) {
        catalogUfRef.current = uf.toUpperCase();
        setCatalogCities([]);
        setCitiesLoadError(true);
        setErrors((prev) => ({
          ...prev,
          addressCity:
            "Não foi possível carregar as cidades. Tente novamente.",
        }));
      }
      toast.error(
        "Não foi possível carregar as cidades. Tente novamente.",
        toastOptions
      );
      return { cities: [], failed: true };
    }
  };

  const fetchAddress = async (postalCode: string) => {
    const loadId = ++cepLoadIdRef.current;
    const isCurrent = () =>
      loadId === cepLoadIdRef.current &&
      postalCodeRef.current.replace(/\D/g, "") === postalCode;
    try {
      const response = await axios.get(
        `https://viacep.com.br/ws/${postalCode}/json/`
      );
      if (!isCurrent()) return;
      const data = response.data;
      if (data.erro) {
        rejectedCepRef.current = postalCode;
        setAddressFromCep(false);
        catalogUfRef.current = "";
        setCatalogCities([]);
        setFormData((prevFormData) => ({
          ...prevFormData,
          address: {
            ...prevFormData.address,
            street: "",
            neighborhood: "",
            state: "",
            city: "",
            cityEntity: null,
          },
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          addressPostalCode: "CEP inválido",
        }));
      } else {
        const loadedCities = await loadCitiesForUf(data.uf);
        if (!isCurrent()) return;
        if (loadedCities.failed) {
          setFormData((prevFormData) => ({
            ...prevFormData,
            address: {
              ...prevFormData.address,
              street: data.logradouro,
              neighborhood: data.bairro,
              state: data.uf,
              city: "",
              cityEntity: null,
            },
          }));
          rejectedCepRef.current = null;
          setAddressFromCep(false);
          return;
        }
        const cities = loadedCities.cities;
        const matchedCity = cities.find(
          (city) => foldCityName(city.name || "") === foldCityName(data.localidade || "")
        );
        setFormData((prevFormData) => ({
          ...prevFormData,
          address: {
            ...prevFormData.address,
            street: data.logradouro,
            neighborhood: data.bairro,
            state: data.uf,
            city: matchedCity?.name || "",
            cityEntity: matchedCity ? { id: matchedCity.id } : null,
          },
        }));
        rejectedCepRef.current = null;
        setAddressFromCep(!!matchedCity);
        setErrors((prevErrors) => {
          const {
            addressPostalCode,
            addressCity,
            addressState,
            ...rest
          } = prevErrors;
          return rest;
        });
        if (!matchedCity) {
          toast.error(
            "Não encontramos essa cidade na lista. Selecione manualmente.",
            toastOptions
          );
        }
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error fetching CEP:", error);
      rejectedCepRef.current = null;
      setAddressFromCep(false);
      toast.error(
        "Não foi possível consultar o CEP. Preencha UF e cidade para continuar.",
        toastOptions
      );
    }
  };

  const handleAddressChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "state") {
      setAddressFromCep(false);
      catalogUfRef.current = "";
      setCatalogCities([]);
      cepLoadIdRef.current += 1;
      citiesLoadIdRef.current += 1;
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          state: value,
          city: "",
          cityEntity: null,
        },
      });
      validateRequiredFields("address.state", value);
      return;
    }

    if (name === "postalCode") {
      const masked = formatCep(value);
      const cep = masked.replace(/\D/g, "");
      postalCodeRef.current = masked;
      rejectedCepRef.current = null;
      setAddressFromCep(false);
      catalogUfRef.current = "";
      setCatalogCities([]);
      cepLoadIdRef.current += 1;
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          postalCode: masked,
          street: "",
          neighborhood: "",
          state: "",
          city: "",
          cityEntity: null,
        },
      });
      setErrors((prevErrors) => {
        const { addressPostalCode, ...rest } = prevErrors;
        return rest;
      });
      if (cep.length === 8) {
        fetchAddress(cep);
      }
      return;
    }

    // If the user types in an address field while a CEP lookup is pending,
    // invalidate the pending fetch so its response does not overwrite the
    // manually entered value.
    cepLoadIdRef.current += 1;
    setFormData({
      ...formData,
      address: { ...formData.address, [name]: value },
    });
    validateRequiredFields(`address.${name}`, value);
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cityId = e.target.value;
    const selectedCity = catalogCities.find(
      (city) => String(city.id) === cityId
    );
    setAddressFromCep(false);
    setFormData({
      ...formData,
      address: {
        ...formData.address,
        city: selectedCity?.name || "",
        cityEntity: selectedCity ? { id: selectedCity.id } : null,
      },
    });
    validateRequiredFields("address.city", selectedCity?.name || "");
  };

  useEffect(() => {
    const uf = formData.address.state;
    if (!uf) {
      return;
    }
    if (catalogUfRef.current === uf.toUpperCase()) {
      return;
    }
    let cancelled = false;
    void loadCitiesForUf(uf).then((loaded) => {
      if (cancelled || loaded.failed) return;
      const cities = loaded.cities;
      setFormData((prev) => {
        if (prev.address.state.toUpperCase() !== uf.toUpperCase()) {
          return prev;
        }
        if (
          prev.address.cityEntity?.id &&
          cities.some((city) => city.id === prev.address.cityEntity?.id)
        ) {
          return prev;
        }
        const matched = cities.find(
          (city) =>
            foldCityName(city.name || "") ===
            foldCityName(prev.address.city || "")
        );
        if (!matched) return prev;
        return {
          ...prev,
          address: {
            ...prev.address,
            city: matched.name,
            cityEntity: { id: matched.id },
          },
        };
      });
    });
    return () => {
      cancelled = true;
    };
  }, [formData.address.state]);

  const handleExperienceTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked, value } = e.target;

    const currentTypes = formData.hearingTypes;
    if (checked && value) {
      currentTypes.push({ id: parseInt(value) });
    }
    if (!checked && value) {
      const index = currentTypes.findIndex(
        (type) => type.id === parseInt(value)
      );
      currentTypes.splice(index, 1);
    }
    setFormData({
      ...formData,
      hearingTypes: currentTypes,
    });
  };

  const handleAgreeToTermsChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;
    let ip = "";

    if (checked) {
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        ip = data.ip;
      } catch (error) {
        console.error("Erro ao obter o IP:", error);
      }
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      agreeToTerms: checked,
      acceptedTermsIp: ip,
    }));
  };

  const createAgent = async () => {
    const pixKey =
      pixType === "Telefone"
        ? canonicalizePixPhone(formData.pixKey)
        : formData.pixKey;
    const response = await api.post(
      `${apiUri}agents`,
      { ...formData, pixKey },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      Object.values(formData.availability).every((value) => value === false)
    ) {
      toast.error(
        "Você deve selecionar pelo menos um turno disponível",
        toastOptions
      );
      return;
    }
    if (
      !formData.preppoCertificateLink ||
      !formData.preppoCertificateLink.startsWith("agents/")
    ) {
      toast.error("Envie o certificado Preppo antes de continuar", toastOptions);
      return;
    }
    if (uploadingCertificate) {
      toast.error("Aguarde o envio do certificado", toastOptions);
      return;
    }
    if (uploadingCv) {
      toast.error("Aguarde o envio do currículo", toastOptions);
      return;
    }
    if (await validateForm()) {
      try {
        await createAgent();
        toast.success(
          "Cadastro realizado com sucesso! Nossa equipe entrará em contato via WhatsApp conforme audiências na sua região são adicionadas.",
          toastOptions
        );
        const handleRedirect = () => {
          window.location.href = "https://preposto.preppo.com.br/";
        };
        setTimeout(() => {
          handleRedirect();
        }, 9000);
      } catch (error: unknown) {
        const apiMessage = (
          error as { response?: { data?: { message?: string | string[] } } }
        )?.response?.data?.message;
        const message = Array.isArray(apiMessage)
          ? apiMessage[0]
          : apiMessage || "Erro ao realizar o cadastro. Tente novamente.";
        toast.error(message, toastOptions);
      }
      return;
    }
  };

  return (
    <>
      <div>{accessToken && <Header />}</div>
      <div className="min-h-screen flex flex-col items-center justify-start w-full px-2 sm:px-4 py-4 sm:py-6 bg-gradient-to-br from-blue-500 to-purple-600">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-4xl mx-auto p-4 sm:p-6 shadow-md mt-4 sm:mt-6 mb-4 sm:mb-6 rounded-xl border-b border-preppoBlue pb-8 sm:pb-12 space-y-4 sm:space-y-6 bg-preppoBlue-light opacity-95"
        >
          <div className="w-full mx-auto mt-2 sm:mt-0 flex flex-col items-center">
            <img
              src="./logo-preppo.png"
              alt="logo preppo"
              className="h-16 sm:h-20 w-auto mb-2 sm:mb-3"
            />
          </div>
          <div className="border-b border-gray-900/10 pb-6 sm:pb-12">
            <h2 className="text-lg font-semibold leading-7 text-preppoBlue text-center">
              Informações Pessoais
            </h2>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-x-5">
              <div className="mt-0 sm:mt-6 w-full sm:w-2/4">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Nome*
                </label>
                <input
                  type="text"
                  name="name"
                  maxLength={100}
                  minLength={3}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  placeholder={agent?.name}
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                {errors.name && (
                  <span className="text-red-500 text-sm">{errors.name}</span>
                )}
              </div>
              <div className="mt-0 sm:mt-6 w-full sm:w-1/4">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  CPF*
                </label>
                <input
                  type="text"
                  name="cpf"
                  maxLength={14}
                  minLength={11}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  placeholder={agent?.cpf}
                  value={formData.cpf}
                  onChange={handleChange}
                  required
                />
                {errors.cpf && (
                  <span className="text-red-500 text-sm">{errors.cpf}</span>
                )}
              </div>
              <div className="mt-0 sm:mt-6 w-full sm:w-1/4">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Nascimento*
                </label>
                <input
                  type="date"
                  name="birthDate"
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  value={formData.birthDate || agent?.birthDate}
                  onChange={handleChange}
                  required
                  max={minDate}
                />
                {errors.birthDate && (
                  <span className="text-red-500 text-sm">
                    {errors.birthDate}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-x-5 mt-2">
              <div className="mt-0 sm:mt-6 w-full">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  E-mail*
                </label>
                <input
                  type="email"
                  name="email"
                  maxLength={50}
                  minLength={3}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  value={formData.email}
                  placeholder={agent?.email}
                  onChange={handleChange}
                  required
                />
                {errors.email && (
                  <span className="text-red-500 text-sm">{errors.email}</span>
                )}
              </div>

              <div className="mt-0 sm:mt-6 w-full">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Telefone*
                </label>
                <input
                  type="text"
                  name="phone"
                  maxLength={20}
                  minLength={3}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  value={formData.phone}
                  placeholder={agent?.phone}
                  onChange={handleChange}
                  required
                />
                {errors.phone && (
                  <span className="text-red-500 text-sm">{errors.phone}</span>
                )}
              </div>
              <div className="mt-0 sm:mt-6 w-full">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Gênero*
                </label>
                <select
                  className="block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6 h-10 sm:h-9"
                  name="gender"
                  value={formData.gender || agent?.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecionar</option>
                  <option value="male">Masculino</option>
                  <option value="female">Feminino</option>
                  <option value="other">Prefiro não informar</option>
                </select>
                {errors.gender && (
                  <span className="text-red-500 text-sm">{errors.gender}</span>
                )}
              </div>
            </div>
          </div>

          <div className="border-b border-gray-900/10 pb-6 sm:pb-12">
            <h2 className="text-lg font-semibold leading-7 text-preppoBlue text-center">
              Endereço
            </h2>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-x-2">
              <div className="w-full sm:w-1/4 col-span-full mt-2 sm:mt-6">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  CEP*
                </label>
                <input
                  type="text"
                  name="postalCode"
                  inputMode="numeric"
                  maxLength={9}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  value={formData.address.postalCode}
                  placeholder="00000-000"
                  onChange={(e) => handleAddressChange(e)}
                  required
                />
                {errors.addressPostalCode && (
                  <span className="text-red-500 text-sm">
                    {errors.addressPostalCode}
                  </span>
                )}
              </div>
              <div className="w-full sm:w-2/4 col-span-full mt-2 sm:mt-6">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Logradouro*
                </label>
                <input
                  type="text"
                  name="street"
                  maxLength={100}
                  minLength={3}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  value={formData.address.street}
                  placeholder={agent?.address.street}
                  onChange={handleAddressChange}
                  required
                />
                {errors.addressStreet && (
                  <span className="text-red-500 text-sm">
                    {errors.addressStreet}
                  </span>
                )}
              </div>
              <div className="w-full sm:w-1/4 col-span-full mt-2 sm:mt-6">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Número*
                </label>
                <input
                  type="number"
                  name="number"
                  maxLength={10}
                  minLength={1}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  value={formData.address.number}
                  placeholder={agent?.address.number}
                  onChange={(e) => handleAddressChange(e)}
                  required
                />
                {errors.addressNumber && (
                  <span className="text-red-500 text-sm">
                    {errors.addressNumber}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-x-2">
              <div className="col-span-1 mt-2 sm:mt-6">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Complemento
                </label>
                <input
                  type="text"
                  name="additionalAddressInfo"
                  maxLength={50}
                  minLength={1}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  value={formData.address.additionalAddressInfo}
                  placeholder={agent?.address.additionalAddressInfo}
                  onChange={handleAddressChange}
                />
              </div>
              <div className="col-span-1 mt-2 sm:mt-6">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Bairro*
                </label>
                <input
                  type="text"
                  name="neighborhood"
                  maxLength={50}
                  minLength={2}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  value={formData.address.neighborhood}
                  placeholder={agent?.address.neighborhood}
                  onChange={handleAddressChange}
                  required
                />
                {errors.addressNeighborhood && (
                  <span className="text-red-500 text-sm">
                    {errors.addressNeighborhood}
                  </span>
                )}
              </div>
              <div className="col-span-1 mt-2 sm:mt-6">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Cidade*
                </label>
                <select
                  name="city"
                  className="block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6 h-10 sm:h-9 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={formData.address.cityEntity?.id ?? ""}
                  onChange={handleCityChange}
                  disabled={!formData.address.state || addressFromCep}
                  required={!addressFromCep}
                >
                  <option value="">
                    {formData.address.state
                      ? "Selecione uma cidade"
                      : "Selecione o estado primeiro"}
                  </option>
                  {catalogCities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.addressCity && (
                  <span className="text-red-500 text-sm">
                    {errors.addressCity}
                  </span>
                )}
                {citiesLoadError && formData.address.state && (
                  <button
                    type="button"
                    className="mt-1 text-sm text-preppoBlue underline"
                    onClick={() => {
                      catalogUfRef.current = "";
                      void loadCitiesForUf(formData.address.state);
                    }}
                  >
                    Tentar carregar as cidades novamente
                  </button>
                )}
              </div>
              <div className="col-span-1 mt-2 sm:mt-6">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Estado*
                </label>
                <select
                  className="block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6 h-10 sm:h-9 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  name="state"
                  value={formData.address.state || agent?.address.state || ""}
                  onChange={handleAddressChange}
                  disabled={addressFromCep}
                  required
                >
                  <option value="">Selecionar</option>
                  {catalogStates.length > 0
                    ? catalogStates
                        .filter((state) => state.slug)
                        .map((state) => (
                          <option
                            key={state.id}
                            value={state.slug.toUpperCase()}
                          >
                            {state.name || state.slug}
                          </option>
                        ))
                    : [
                        ["AC", "Acre"],
                        ["AL", "Alagoas"],
                        ["AP", "Amapá"],
                        ["AM", "Amazonas"],
                        ["BA", "Bahia"],
                        ["CE", "Ceará"],
                        ["DF", "Distrito Federal"],
                        ["ES", "Espírito Santo"],
                        ["GO", "Goiás"],
                        ["MA", "Maranhão"],
                        ["MT", "Mato Grosso"],
                        ["MS", "Mato Grosso do Sul"],
                        ["MG", "Minas Gerais"],
                        ["PA", "Pará"],
                        ["PB", "Paraíba"],
                        ["PR", "Paraná"],
                        ["PE", "Pernambuco"],
                        ["PI", "Piauí"],
                        ["RJ", "Rio de Janeiro"],
                        ["RN", "Rio Grande do Norte"],
                        ["RS", "Rio Grande do Sul"],
                        ["RO", "Rondônia"],
                        ["RR", "Roraima"],
                        ["SC", "Santa Catarina"],
                        ["SP", "São Paulo"],
                        ["SE", "Sergipe"],
                        ["TO", "Tocantins"],
                      ].map(([slug, name]) => (
                        <option key={slug} value={slug}>
                          {name}
                        </option>
                      ))}
                </select>
                {errors.addressState && (
                  <span className="text-red-500 text-sm">
                    {errors.addressState}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="border-b border-gray-900/10 pb-6 sm:pb-12">
            <h2 className="text-lg font-semibold leading-7 text-preppoBlue text-center">
              Informações Profissionais
            </h2>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-x-5">
              <div className="mt-4 sm:mt-6 w-full lg:w-2/3">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Currículo
                </label>
                <input
                  type="file"
                  name="cvLink"
                  id="cvLink"
                  className="hidden"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleCvFileChange}
                  disabled={uploadingCv}
                />
                <div className="flex flex-col sm:flex-row items-start sm:items-center mt-2 gap-2">
                  <label
                    htmlFor="cvLink"
                    className={`inline-block rounded-lg px-4 py-3 sm:px-3 sm:py-2 text-sm font-semibold text-white min-h-[44px] sm:min-h-0 flex items-center justify-center ${
                      uploadingCv
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600 transition-all cursor-pointer"
                    }`}
                  >
                    {uploadingCv ? "Enviando..." : "Escolher arquivo"}
                  </label>
                  <span className="text-sm text-gray-700 border-dotted border-2 border-gray-900/10 p-2 rounded-md flex-1 min-h-[44px] sm:min-h-0 flex items-center">
                    {cvFileName ||
                      (formData.cvLink?.startsWith("agents/")
                        ? "Currículo enviado"
                        : "Nenhum arquivo selecionado")}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Formatos: PDF, DOC ou DOCX. Máximo 20MB.
                </p>
              </div>
              <div className="mt-4 sm:mt-6 w-full lg:w-1/3">
                <label className="mb-2 block text-sm font-medium leading-6 text-preppoBlue">
                  Portfólio
                </label>
                <input
                  type="text"
                  name="portfolio"
                  minLength={1}
                  maxLength={100}
                  className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6"
                  value={formData.portfolio}
                  placeholder={agent?.portfolio}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="flex flex-col mt-4 sm:mt-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <label className="block text-sm font-medium leading-6 text-preppoBlue">
                  Possui experiência como preposto?*
                </label>
                <div className="flex items-center space-x-6 sm:space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="hasExperience"
                      value="true"
                      checked={formData.hasExperience}
                      onChange={handleChange}
                      className="form-radio h-5 w-5 text-preppoBlue"
                    />
                    <span className="ml-2 text-preppoBlue text-base sm:text-sm">Sim</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="hasExperience"
                      value="false"
                      checked={!formData.hasExperience}
                      onChange={handleChange}
                      className="form-radio h-5 w-5 text-preppoBlue"
                    />
                    <span className="ml-2 text-preppoBlue text-base sm:text-sm">Não</span>
                  </label>
                </div>
              </div>
              {formData.hasExperience && (
                <div className="mt-4 mx-2 sm:m-4">
                  <label className="block text-sm leading-6 text-preppoBlue mb-3">
                    Em qual tipo de audiência*?
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    <label className="inline-flex items-center text-sm font-medium leading-6 text-gray-500 min-h-[44px] sm:min-h-0">
                      <input
                        type="checkbox"
                        id="conciliacao"
                        name="conciliacao"
                        value={1}
                        checked={
                          formData.hearingTypes.some((type) => type.id === 1) ||
                          agent?.hearingTypes.some((el: any) => el.id === 1)
                        }
                        onChange={handleExperienceTypeChange}
                        className="form-checkbox h-5 w-5 text-preppoBlue"
                      />
                      <span className="ml-3 text-preppoBlue text-base sm:text-sm">Conciliação</span>
                    </label>
                    <label className="inline-flex items-center text-sm font-medium leading-6 text-gray-500 min-h-[44px] sm:min-h-0">
                      <input
                        type="checkbox"
                        id="instrucao"
                        name="instrucao"
                        value={3}
                        checked={
                          formData.hearingTypes.some((type) => type.id === 3) ||
                          agent?.hearingTypes.some((el: any) => el.id === 3)
                        }
                        onChange={handleExperienceTypeChange}
                        className="form-checkbox h-5 w-5 text-preppoBlue"
                      />
                      <span className="ml-3 text-preppoBlue text-base sm:text-sm">Instrução</span>
                    </label>
                    <label className="inline-flex items-center text-sm font-medium leading-6 text-gray-500 min-h-[44px] sm:min-h-0">
                      <input
                        type="checkbox"
                        id="inicial"
                        name="inicial"
                        value={4}
                        checked={
                          formData.hearingTypes.some((type) => type.id === 4) ||
                          agent?.hearingTypes.some((el: any) => el.id === 4)
                        }
                        onChange={handleExperienceTypeChange}
                        className="form-checkbox h-5 w-5 text-preppoBlue"
                      />
                      <span className="ml-3 text-preppoBlue text-base sm:text-sm">Inicial</span>
                    </label>
                    <label className="inline-flex items-center text-sm font-medium leading-6 text-gray-500 min-h-[44px] sm:min-h-0">
                      <input
                        type="checkbox"
                        id="una"
                        name="una"
                        value={2}
                        checked={
                          formData.hearingTypes.some((type) => type.id === 2) ||
                          agent?.hearingTypes.some((el: any) => el.id === 2)
                        }
                        onChange={handleExperienceTypeChange}
                        className="form-checkbox h-5 w-5 text-preppoBlue"
                      />
                      <span className="ml-3 text-preppoBlue text-base sm:text-sm">Una</span>
                    </label>
                    {errors.experienceTypes && (
                      <span className="text-red-500 text-sm">
                        {errors.experienceTypes}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col mt-4 sm:mt-6">
              <div className="mt-4 mx-2 sm:m-4">
                <label className="block text-sm font-medium leading-6 text-preppoBlue mb-2">
                  Certificado do Curso para Preposto da Preppo*
                </label>
                <input
                  type="file"
                  name="preppoCertificateLink"
                  id="preppoCertificateLink"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={handleCertificateFileChange}
                  disabled={uploadingCertificate}
                />
                <div className="flex flex-col sm:flex-row items-start sm:items-center mt-2 gap-2">
                  <label
                    htmlFor="preppoCertificateLink"
                    className={`inline-block rounded-lg px-4 py-3 sm:px-3 sm:py-2 text-sm font-semibold text-white min-h-[44px] sm:min-h-0 flex items-center justify-center ${
                      uploadingCertificate
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600 transition-all cursor-pointer"
                    }`}
                  >
                    {uploadingCertificate ? "Enviando..." : "Escolher arquivo"}
                  </label>
                  <span className="text-sm text-gray-700 border-dotted border-2 border-gray-900/10 p-2 rounded-md flex-1 min-h-[44px] sm:min-h-0 flex items-center">
                    {certificateFileName ||
                      (formData.preppoCertificateLink?.startsWith("agents/")
                        ? "Certificado enviado"
                        : "Nenhum arquivo selecionado")}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Formatos: PDF, JPG ou PNG. Máximo 20MB.
                </p>
                {errors.preppoCertificateLink && (
                  <span className="text-red-500 text-sm">
                    {errors.preppoCertificateLink}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-lg font-semibold leading-7 text-preppoBlue text-center">
              Informação Financeira
            </h2>

            <div className="mt-1 sm:mt-6 w-full">
              <label className="block text-sm font-medium leading-6 text-preppoBlue mb-1 sm:mb-3">
                Tipo chave pix*
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 sm:gap-x-4" role="radiogroup" aria-label="Tipo de chave PIX">
                <label
                  className={`w-full rounded-lg border p-2 sm:p-2 flex items-center gap-1 sm:gap-2 cursor-pointer transition-all ${
                    pixType === "Telefone"
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-200"
                      : "border-gray-300 bg-white hover:border-blue-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="pixType"
                    value="Telefone"
                    checked={pixType === "Telefone"}
                    onChange={handlePixTypeChange}
                    className="h-4 w-4 sm:h-5 sm:w-5 text-preppoBlue"
                    aria-label="Telefone"
                  />
                  <span className="text-preppoBlue text-sm sm:text-sm">Telefone</span>
                </label>

                <label
                  className={`w-full rounded-lg border p-2 sm:p-2 flex items-center gap-1 sm:gap-2 cursor-pointer transition-all ${
                    pixType === "CPF"
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-200"
                      : "border-gray-300 bg-white hover:border-blue-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="pixType"
                    value="CPF"
                    checked={pixType === "CPF"}
                    onChange={handlePixTypeChange}
                    className="h-4 w-4 sm:h-5 sm:w-5 text-preppoBlue"
                    aria-label="CPF"
                  />
                  <span className="text-preppoBlue text-sm sm:text-sm">CPF</span>
                </label>

                <label
                  className={`w-full rounded-lg border p-2 sm:p-2 flex items-center gap-1 sm:gap-2 cursor-pointer transition-all ${
                    pixType === "E-mail"
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-200"
                      : "border-gray-300 bg-white hover:border-blue-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="pixType"
                    value="E-mail"
                    checked={pixType === "E-mail"}
                    onChange={handlePixTypeChange}
                    className="h-4 w-4 sm:h-5 sm:w-5 text-preppoBlue"
                    aria-label="E-mail"
                  />
                  <span className="text-preppoBlue text-sm sm:text-sm">E-mail</span>
                </label>

              </div>

              <div className="mt-4 p-3 bg-amber-50 border-l-4 border-amber-500 rounded-md">
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 text-lg">⚠️</span>
                  <p className="text-sm text-amber-800">
                    <strong>ATENÇÃO:</strong> Não é permitido cadastrar chave PIX de terceiros ou de pessoas jurídicas. A chave PIX deve estar em seu nome (CPF).
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full col-span-full mt-4 sm:mt-6">
              <label className="block text-sm font-medium leading-6 text-preppoBlue mb-2">
                Chave Pix*
              </label>
              <div className="text-xs text-gray-500 mb-3 space-y-1">
                <div><strong>CPF:</strong> Apenas os 11 dígitos. Ex: 12345678900</div>
                <div><strong>Telefone:</strong> +55 + DDD + número. Ex: 5511999999999</div>
                <div><strong>E-mail:</strong> Seu e-mail válido. Ex: seu.email@exemplo.com</div>
              </div>
              <input
                type={pixType === "E-mail" ? "email" : "text"}
                name="pixKey"
                inputMode={pixType === "E-mail" ? "email" : "numeric"}
                autoComplete={pixType === "E-mail" ? "email" : "off"}
                maxLength={pixType === "CPF" ? 14 : pixType === "Telefone" ? 20 : 100}
                readOnly={!pixType}
                required
                className="px-2 sm:px-1 block w-full rounded-md border-0 py-2 sm:py-1.5 text-preppoBlue shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-preppoBlue text-base sm:text-sm sm:leading-6 read-only:bg-gray-100"
                value={
                  pixType === "Telefone" && !pixPhoneFocused
                    ? displayPixPhone(formData.pixKey)
                    : formData.pixKey
                }
                placeholder={
                  pixType === "CPF"
                    ? "12345678900"
                    : pixType === "Telefone"
                      ? "+55 (11) 99999-9999"
                      : pixType === "E-mail"
                        ? "seu.email@exemplo.com"
                        : "Selecione o tipo de chave"
                }
                onFocus={() => {
                  if (pixType === "Telefone") setPixPhoneFocused(true);
                }}
                onChange={handlePixKeyChange}
                onBlur={() => {
                  if (pixType !== "Telefone") return;
                  setPixPhoneFocused(false);
                  setFormData((prev) => ({
                    ...prev,
                    pixKey: canonicalizePixPhone(prev.pixKey),
                  }));
                }}
              />
              {errors.pixKey && (
                <span className="text-red-500 text-sm">{errors.pixKey}</span>
              )}
            </div>
          </div>
          <HearingForm
            setParentState={setStateWrapper}
            parentState={formData || agent}
          />
          <div className="mt-6 w-full">
            <label className="flex items-start sm:items-center gap-2 sm:gap-3 text-sm font-medium leading-6 text-preppoBlue cursor-pointer">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleAgreeToTermsChange}
                required
                className="mt-1 sm:mt-0 h-5 w-5 text-preppoBlue flex-shrink-0"
              />
              <span className="text-sm leading-relaxed">
                Li e estou de acordo com os{" "}
                <a
                  href="./tc-preppo.pdf"
                  target="_blank"
                  className="text-preppoBlue underline hover:text-amber-600 transition-colors"
                >
                  Termos e Condições Gerais de Uso de Prestação de Serviços de
                  Prepostos*
                </a>
              </span>
            </label>
            {errors.agreeToTerms && (
              <span className="text-red-500 text-sm mt-2 block">
                {errors.agreeToTerms}
              </span>
            )}
          </div>
          <div className="mt-6 sm:mt-8 flex items-center justify-center gap-x-6 mb-4 sm:mb-6">
            <button
              type="submit"
              className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 rounded-lg font-semibold text-white transition-all px-6 py-3 sm:px-4 sm:py-2 min-h-[48px] sm:min-h-0"
            >
              Enviar
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AgentForm;
